import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoAddOutline } from "react-icons/io5";
import {
  MdClear,
  MdDriveFileRenameOutline,
  MdExpandMore,
  MdChevronRight,
  MdFolder,
  MdFolderOpen,
  MdCreateNewFolder,
} from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaUserFriends } from "react-icons/fa";

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Button from "../Utilities/Button";
import RightPanelLayout from "../Layout/RightPanelLayout";
import Alert from "../Utilities/Alert";
import Toast from "../Utilities/Toast";
import Modal from "../Utilities/Modal";
import {
  createRecommendedPrompt,
  createRecommendedPromptFolder,
  deleteRecommendedPrompt,
  deleteRecommendedPromptFolder,
  fetchRecommendedPromptFolders,
  fetchRecommendedPrompts,
  renameRecommendedPromptFolder,
  reorderRecommendedPromptFolders,
  reorderRecommendedPrompts,
  updateRecommendedPrompt,
} from "./recommendedPromptsApi";
import styles from "./style/RecommendedPromptsList.module.scss";

const ROOT_CONTAINER = "root";

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const getRowId = (row) => row?._id || row?.id || "";

const getOrderValue = (row) => {
  const n = Number(row?.order);
  return Number.isFinite(n) ? n : 0;
};

const getCreatedAtMs = (row) => {
  const t = Date.parse(row?.createdAt);
  return Number.isFinite(t) ? t : 0;
};

const sortByOrder = (arr) => {
  const next = [...arr];
  next.sort((a, b) => {
    const ao = getOrderValue(a);
    const bo = getOrderValue(b);
    if (ao !== bo) return ao - bo;
    return getCreatedAtMs(b) - getCreatedAtMs(a);
  });
  return next;
};

const getPromptContainerId = (prompt) =>
  prompt?.folderId ? `folder:${prompt.folderId}` : ROOT_CONTAINER;

const containerIdToFolderId = (containerId) =>
  containerId && containerId.startsWith("folder:")
    ? containerId.slice("folder:".length)
    : null;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PromptRow = ({ id, item, index, onEdit, onDelete, containerId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `prompt:${id}`,
    data: { type: "prompt", promptId: id, containerId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.promptRow} ${isDragging ? styles.rowDraggingOrigin : ""}`}
    >
      <button
        type='button'
        className={styles.dragHandle}
        aria-label='拖曳排序'
        title='拖曳以排序或移動至資料夾'
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      <div className={styles.orderCell}>{index + 1}</div>
      <div className={styles.nameCell}>{item?.name || "-"}</div>
      <div className={styles.descCell}>{item?.description || "-"}</div>
      <div className={styles.actions}>
        <button
          type='button'
          className={styles.editButton}
          onClick={() => onEdit(item)}
          aria-label='編輯提示詞'
        >
          <MdDriveFileRenameOutline />
        </button>
        <button
          type='button'
          className={styles.deleteButton}
          onClick={() => onDelete(id)}
          aria-label='刪除提示詞'
        >
          <RiDeleteBin6Line />
        </button>
        {Array.isArray(item?.sharedTo) && item.sharedTo.filter(Boolean).length > 0 ? (
          <span
            className={styles.sharedToInline}
            title={`sharedTo: ${item.sharedTo.filter(Boolean).length}`}
            aria-label={`sharedTo ${item.sharedTo.filter(Boolean).length}`}
          >
            <FaUserFriends />
            <span className={styles.sharedToInlineCount}>
              {item.sharedTo.filter(Boolean).length}
            </span>
          </span>
        ) : null}
      </div>
    </div>
  );
};

const PromptDragOverlay = ({ item }) => (
  <div className={`${styles.promptRow} ${styles.dragOverlayItem}`}>
    <div className={styles.dragHandle} aria-hidden='true'>
      ⋮⋮
    </div>
    <div className={styles.orderCell}>·</div>
    <div className={styles.nameCell}>{item?.name || "-"}</div>
    <div className={styles.descCell}>{item?.description || "-"}</div>
  </div>
);

const FolderDropZone = ({ containerId, active, label }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `container:${containerId}`,
    data: { type: "container", containerId },
  });
  if (!active) return null;
  const cls = [
    styles.folderDropZone,
    styles.folderDropZoneActive,
    isOver ? styles.folderDropZoneOver : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div ref={setNodeRef} className={cls}>
      <span className={styles.emptyHint}>{label || "拖曳提示詞至此"}</span>
    </div>
  );
};

const FolderRow = ({
  folder,
  expanded,
  onToggle,
  onRename,
  onDelete,
  promptIds,
  promptsInFolder,
  onEditPrompt,
  onDeletePrompt,
  isDropTargetActive,
  hideBody,
}) => {
  const containerId = `folder:${folder._id}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `folder:${folder._id}`,
    data: { type: "folder", folderId: folder._id, containerId },
  });

  const { setNodeRef: setHeaderDroppableRef, isOver: isHeaderOver } = useDroppable({
    id: `folder-header:${folder._id}`,
    data: { type: "folder-header", folderId: folder._id, containerId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.folderBlock} ${isDragging ? styles.folderDragging : ""}`}
    >
      <div
        ref={setHeaderDroppableRef}
        className={`${styles.folderHeader} ${
          isDropTargetActive ? styles.folderHeaderHitZone : ""
        } ${
          isHeaderOver && isDropTargetActive ? styles.folderHeaderOver : ""
        }`}
      >
        <button
          type='button'
          className={styles.dragHandle}
          aria-label='拖曳資料夾'
          title='拖曳以重新排序資料夾'
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
        <button
          type='button'
          className={styles.folderToggle}
          onClick={() => onToggle(folder._id)}
          aria-label={expanded ? "收合資料夾" : "展開資料夾"}
        >
          {expanded ? <MdExpandMore /> : <MdChevronRight />}
        </button>
        <div className={styles.folderIcon}>
          {expanded ? <MdFolderOpen /> : <MdFolder />}
        </div>
        <div className={styles.folderName}>{folder.name}</div>
        <div className={styles.folderCount}>
          {promptsInFolder.length} 筆
        </div>
        <div className={styles.folderActions}>
          <button
            type='button'
            className={styles.editButton}
            onClick={() => onRename(folder)}
            aria-label='重新命名'
          >
            <MdDriveFileRenameOutline />
          </button>
          <button
            type='button'
            className={styles.deleteButton}
            onClick={() => onDelete(folder)}
            aria-label='刪除資料夾'
          >
            <RiDeleteBin6Line />
          </button>
        </div>
      </div>

      {expanded ? (
        <div
          className={styles.folderBody}
          style={hideBody ? { display: "none" } : undefined}
        >
          <SortableContext
            items={promptIds}
            strategy={verticalListSortingStrategy}
          >
            {promptsInFolder.map((p, idx) => (
              <PromptRow
                key={getRowId(p)}
                id={getRowId(p)}
                item={p}
                index={idx}
                containerId={containerId}
                onEdit={onEditPrompt}
                onDelete={onDeletePrompt}
              />
            ))}
            <FolderDropZone
              containerId={containerId}
              active={isDropTargetActive}
            />
          </SortableContext>
        </div>
      ) : isHeaderOver ? (
        <div className={styles.folderClosedHint}>
          放開以加入「{folder.name}」
        </div>
      ) : null}
    </div>
  );
};

const FolderDragOverlay = ({ folder }) => (
  <div className={`${styles.folderHeader} ${styles.dragOverlayItem}`}>
    <div className={styles.dragHandle} aria-hidden='true'>⋮⋮</div>
    <div className={styles.folderIcon}>
      <MdFolder />
    </div>
    <div className={styles.folderName}>{folder?.name || "-"}</div>
  </div>
);

const RecommendedPromptsList = () => {
  const [folders, setFolders] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [loading, setLoading] = useState(true);

  // Prompt CRUD modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("new");
  const [editId, setEditId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formEmails, setFormEmails] = useState([]);
  const [formEmailInput, setFormEmailInput] = useState("");
  const [formFolderId, setFormFolderId] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Prompt delete
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Folder modal state
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState("new");
  const [folderEditId, setFolderEditId] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [folderSubmitting, setFolderSubmitting] = useState(false);
  const [folderDeleteTarget, setFolderDeleteTarget] = useState(null);
  const [folderDeleteSubmitting, setFolderDeleteSubmitting] = useState(false);

  // DnD state
  const [activeId, setActiveId] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const reorderInFlightRef = useRef(false);

  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [promptsData, foldersData] = await Promise.all([
        fetchRecommendedPrompts(),
        fetchRecommendedPromptFolders().catch(() => []),
      ]);
      const sortedFolders = sortByOrder(normalizeRows(foldersData));
      const sortedPrompts = sortByOrder(normalizeRows(promptsData));
      setFolders(sortedFolders);
      setPrompts(sortedPrompts);
      setExpandedFolders((prev) => {
        const next = { ...prev };
        sortedFolders.forEach((f) => {
          if (next[f._id] === undefined) next[f._id] = false;
        });
        return next;
      });
    } catch (err) {
      setFolders([]);
      setPrompts([]);
      showToast(err?.message || "載入失敗", true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const promptsByContainer = useMemo(() => {
    const map = { [ROOT_CONTAINER]: [] };
    folders.forEach((f) => {
      map[`folder:${f._id}`] = [];
    });
    prompts.forEach((p) => {
      const cid = getPromptContainerId(p);
      if (!map[cid]) map[cid] = [];
      map[cid].push(p);
    });
    Object.keys(map).forEach((k) => {
      map[k] = sortByOrder(map[k]);
    });
    return map;
  }, [prompts, folders]);

  const folderIds = useMemo(
    () => folders.map((f) => `folder:${f._id}`),
    [folders]
  );

  const rootPromptIds = useMemo(
    () => (promptsByContainer[ROOT_CONTAINER] || []).map((p) => `prompt:${getRowId(p)}`),
    [promptsByContainer]
  );

  const findPromptById = useCallback(
    (id) => prompts.find((p) => getRowId(p) === id) || null,
    [prompts]
  );

  // ---------- Prompt modal helpers ----------
  const resetPromptForm = () => {
    setFormName("");
    setFormDescription("");
    setFormContent("");
    setFormEmails([]);
    setFormEmailInput("");
    setFormFolderId("");
    setEditId(null);
    setModalMode("new");
  };

  const openCreatePromptModal = (folderId = "") => {
    resetPromptForm();
    setFormFolderId(folderId || "");
    setModalMode("new");
    setModalOpen(true);
  };

  const openEditPromptModal = (item) => {
    setModalMode("edit");
    setEditId(getRowId(item) || null);
    setFormName(item?.name || "");
    setFormDescription(item?.description || "");
    setFormContent(item?.content || "");
    setFormEmails(Array.isArray(item?.sharedTo) ? item.sharedTo.filter(Boolean) : []);
    setFormEmailInput("");
    setFormFolderId(item?.folderId || "");
    setModalOpen(true);
  };

  const closePromptModal = () => {
    if (formSubmitting) return;
    setModalOpen(false);
  };

  const addFormEmail = (raw) => {
    const parts = raw
      .split(/[,\n;]/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && EMAIL_REGEX.test(s));
    if (parts.length === 0) return;
    setFormEmails((prev) => {
      const next = [...prev];
      parts.forEach((e) => {
        if (!next.includes(e)) next.push(e);
      });
      return next;
    });
    setFormEmailInput("");
  };

  const handleFormEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (formEmailInput.trim()) addFormEmail(formEmailInput);
    }
  };

  const handleFormEmailBlur = () => {
    if (formEmailInput.trim()) addFormEmail(formEmailInput);
  };

  const removeFormEmail = (email) => {
    setFormEmails((prev) => prev.filter((e) => e !== email));
  };

  const editFormEmail = (email) => {
    setFormEmails((prev) => prev.filter((e) => e !== email));
    setFormEmailInput(email);
    setTimeout(
      () => document.getElementById("recommended-prompt-sharedTo")?.focus(),
      0
    );
  };

  const handleSubmitPrompt = async () => {
    if (!formName.trim() || !formContent.trim()) {
      showToast("名稱與內容為必填", true);
      return;
    }

    setFormSubmitting(true);
    try {
      const pendingEmails =
        formEmailInput.trim() &&
        EMAIL_REGEX.test(formEmailInput.trim().toLowerCase())
          ? [...formEmails, formEmailInput.trim().toLowerCase()]
          : formEmails;

      const payload = {
        name: formName.trim(),
        description: formDescription.trim(),
        content: formContent.trim(),
        sharedTo: pendingEmails,
        folderId: formFolderId || null,
      };

      if (modalMode === "edit" && editId) {
        await updateRecommendedPrompt(editId, payload);
        showToast("已更新提示詞");
      } else {
        await createRecommendedPrompt(payload);
        showToast("已新增提示詞");
      }

      setModalOpen(false);
      resetPromptForm();
      await loadAll();
    } catch (err) {
      showToast(err?.message || "儲存失敗，請稍後再試", true);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeletePrompt = async () => {
    if (!deleteTargetId || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await deleteRecommendedPrompt(deleteTargetId);
      setDeleteTargetId(null);
      showToast("已刪除提示詞");
      await loadAll();
    } catch (err) {
      showToast(err?.message || "刪除失敗，請稍後再試", true);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ---------- Folder modal helpers ----------
  const openCreateFolderModal = () => {
    setFolderModalMode("new");
    setFolderEditId(null);
    setFolderName("");
    setFolderModalOpen(true);
  };

  const openRenameFolderModal = (folder) => {
    setFolderModalMode("edit");
    setFolderEditId(folder._id);
    setFolderName(folder.name || "");
    setFolderModalOpen(true);
  };

  const closeFolderModal = () => {
    if (folderSubmitting) return;
    setFolderModalOpen(false);
  };

  const handleSubmitFolder = async () => {
    const name = folderName.trim();
    if (!name) {
      showToast("資料夾名稱為必填", true);
      return;
    }
    setFolderSubmitting(true);
    try {
      if (folderModalMode === "edit" && folderEditId) {
        await renameRecommendedPromptFolder(folderEditId, { name });
        showToast("已更新資料夾名稱");
      } else {
        await createRecommendedPromptFolder({ name });
        showToast("已新增資料夾");
      }
      setFolderModalOpen(false);
      await loadAll();
    } catch (err) {
      showToast(err?.message || "儲存失敗，請稍後再試", true);
    } finally {
      setFolderSubmitting(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderDeleteTarget || folderDeleteSubmitting) return;
    setFolderDeleteSubmitting(true);
    try {
      await deleteRecommendedPromptFolder(folderDeleteTarget._id);
      setFolderDeleteTarget(null);
      showToast("已刪除資料夾與其中的提示詞");
      await loadAll();
    } catch (err) {
      showToast(err?.message || "刪除失敗，請稍後再試", true);
    } finally {
      setFolderDeleteSubmitting(false);
    }
  };

  const toggleFolderExpanded = (id) => {
    setExpandedFolders((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ---------- DnD logic ----------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const collisionDetection = useCallback((args) => {
    const activeType = args.active?.data?.current?.type;
    const activeContainerId = args.active?.data?.current?.containerId;
    const getData = (id) => {
      const c = args.droppableContainers.find((dc) => dc.id === id);
      return c?.data?.current || {};
    };

    if (activeType === "folder") {
      const filtered = args.droppableContainers.filter(
        (c) => c.data?.current?.type === "folder"
      );
      const folderArgs = { ...args, droppableContainers: filtered };
      const pw = pointerWithin(folderArgs);
      if (pw.length > 0) return pw;
      return closestCenter(folderArgs);
    }

    // Prompt drag — same-container prompt hits win first (in-folder reorder),
    // otherwise bias toward folder / folder-header / container drop slots.
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) {
      const sameContainerPromptHits = pointerHits.filter((c) => {
        const d = getData(c.id);
        return d.type === "prompt" && d.containerId === activeContainerId;
      });
      if (sameContainerPromptHits.length > 0) return sameContainerPromptHits;
      const folderHits = pointerHits.filter((c) => {
        const t = getData(c.id).type;
        return t === "folder" || t === "folder-header" || t === "container";
      });
      if (folderHits.length > 0) return folderHits;
      return pointerHits;
    }

    // Try rect intersection — same-container prompts first, then folders, then anything.
    const allIntersections = rectIntersection(args);
    if (allIntersections.length > 0) {
      const sameContainerPromptIntersects = allIntersections.filter((c) => {
        const d = getData(c.id);
        return d.type === "prompt" && d.containerId === activeContainerId;
      });
      if (sameContainerPromptIntersects.length > 0)
        return sameContainerPromptIntersects;
      const folderIntersects = allIntersections.filter((c) => {
        const t = getData(c.id).type;
        return t === "folder" || t === "folder-header" || t === "container";
      });
      if (folderIntersects.length > 0) return folderIntersects;
      return allIntersections;
    }

    // Last resort: closest folder/container by center, otherwise closest of all.
    const folderContainers = args.droppableContainers.filter((c) => {
      const t = c.data?.current?.type;
      return t === "folder" || t === "folder-header" || t === "container";
    });
    if (folderContainers.length > 0) {
      const folderCenter = closestCenter({
        ...args,
        droppableContainers: folderContainers,
      });
      if (folderCenter.length > 0) return folderCenter;
    }
    return closestCenter(args);
  }, []);

  const handleDragStart = (event) => {
    const data = event?.active?.data?.current;
    setActiveId(event?.active?.id || null);
    setActiveType(data?.type || null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveType(null);
  };

  // resolve target container & insert position from over event for prompt drags
  const resolveDropTarget = (overId, overData, activePromptId, activeContainerId) => {
    if (!overId) return null;
    if (overData?.type === "container") {
      const containerId = overData.containerId;
      const arr = promptsByContainer[containerId] || [];
      const filtered = arr.filter((p) => getRowId(p) !== activePromptId);
      // Drops onto 未分類 (root) container drop zone insert at the top so the
      // user immediately sees the prompt they just moved.
      if (containerId === ROOT_CONTAINER) {
        return { containerId, index: 0 };
      }
      return { containerId, index: filtered.length };
    }
    if (overData?.type === "folder-header" || overData?.type === "folder") {
      const containerId = overData.containerId;
      const arr = promptsByContainer[containerId] || [];
      const filtered = arr.filter((p) => getRowId(p) !== activePromptId);
      return { containerId, index: filtered.length };
    }
    if (overData?.type === "prompt") {
      const containerId = overData.containerId;
      const arr = promptsByContainer[containerId] || [];
      const idx = arr.findIndex((p) => getRowId(p) === overData.promptId);
      if (idx < 0) return { containerId, index: arr.length };
      // when dragging within same container & moving down, dnd-kit returns
      // the existing index; arrayMove handles it. For cross-container,
      // we insert before the target.
      if (containerId === activeContainerId) {
        return { containerId, index: idx, sameContainer: true };
      }
      return { containerId, index: idx };
    }
    return null;
  };

  const persistPromptMoves = async (containersToPersist, snapshotPrompts) => {
    if (reorderInFlightRef.current) return;
    const items = [];
    containersToPersist.forEach((cid) => {
      const folderId = containerIdToFolderId(cid);
      const arr = (snapshotPrompts.byContainer[cid] || []).slice();
      arr.forEach((p, idx) => {
        items.push({
          id: getRowId(p),
          folderId: folderId,
          order: idx,
        });
      });
    });
    if (items.length === 0) return;

    reorderInFlightRef.current = true;
    try {
      await reorderRecommendedPrompts(items);
    } catch (err) {
      showToast(err?.message || "更新排序失敗，請稍後再試", true);
      await loadAll();
    } finally {
      reorderInFlightRef.current = false;
    }
  };

  const persistFolderOrder = async (orderedFolders) => {
    if (reorderInFlightRef.current) return;
    const items = orderedFolders.map((f, idx) => ({
      id: f._id,
      order: idx,
    }));
    if (items.length === 0) return;
    reorderInFlightRef.current = true;
    try {
      await reorderRecommendedPromptFolders(items);
    } catch (err) {
      showToast(err?.message || "更新資料夾排序失敗", true);
      await loadAll();
    } finally {
      reorderInFlightRef.current = false;
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveType(null);

    if (!active || !over) return;

    const activeData = active?.data?.current;
    const overData = over?.data?.current;

    // Folder reordering
    if (activeData?.type === "folder" && overData?.type === "folder") {
      if (active.id === over.id) return;
      const oldIdx = folders.findIndex((f) => `folder:${f._id}` === active.id);
      const newIdx = folders.findIndex((f) => `folder:${f._id}` === over.id);
      if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
      const next = arrayMove(folders, oldIdx, newIdx).map((f, idx) => ({
        ...f,
        order: idx,
      }));
      setFolders(next);
      void persistFolderOrder(next);
      return;
    }

    // Prompt drop / reorder / move-across-folder
    if (activeData?.type === "prompt") {
      const activePromptId = activeData.promptId;
      const sourceContainerId = activeData.containerId;
      const target = resolveDropTarget(
        over.id,
        overData,
        activePromptId,
        sourceContainerId
      );
      if (!target) return;

      const targetContainerId = target.containerId;

      // Compute new prompt arrangement for affected containers
      const sourceArr = (promptsByContainer[sourceContainerId] || []).slice();
      const sourceIdx = sourceArr.findIndex(
        (p) => getRowId(p) === activePromptId
      );
      if (sourceIdx < 0) return;
      const moving = sourceArr[sourceIdx];

      const containersToPersist = new Set();
      let nextSourceArr;
      let nextTargetArr;

      if (sourceContainerId === targetContainerId) {
        // same-container reorder
        if (sourceIdx === target.index) return;
        nextSourceArr = arrayMove(sourceArr, sourceIdx, target.index);
        nextTargetArr = nextSourceArr;
        containersToPersist.add(sourceContainerId);
      } else {
        // cross-container move
        nextSourceArr = sourceArr.filter(
          (p) => getRowId(p) !== activePromptId
        );
        const targetArr = (promptsByContainer[targetContainerId] || []).slice();
        const insertIdx = Math.min(target.index, targetArr.length);
        const targetFolderId = containerIdToFolderId(targetContainerId);
        const movedItem = { ...moving, folderId: targetFolderId };
        targetArr.splice(insertIdx, 0, movedItem);
        nextTargetArr = targetArr;
        containersToPersist.add(sourceContainerId);
        containersToPersist.add(targetContainerId);
      }

      // Build new flat prompts array with updated order/folderId
      const updatedByContainer = { ...promptsByContainer };
      updatedByContainer[sourceContainerId] = nextSourceArr;
      if (sourceContainerId !== targetContainerId) {
        updatedByContainer[targetContainerId] = nextTargetArr;
      }

      // Reassign order numbers per container, write back
      const flatNext = [];
      Object.keys(updatedByContainer).forEach((cid) => {
        const folderId = containerIdToFolderId(cid);
        updatedByContainer[cid].forEach((p, idx) => {
          flatNext.push({ ...p, folderId: folderId, order: idx });
        });
      });
      // Include any prompts not in any container (shouldn't happen, but safe)
      prompts.forEach((p) => {
        if (!flatNext.some((np) => getRowId(np) === getRowId(p))) {
          flatNext.push(p);
        }
      });

      setPrompts(flatNext);

      // Auto-expand target folder if it was collapsed
      const targetFolderId = containerIdToFolderId(targetContainerId);
      if (targetFolderId && !expandedFolders[targetFolderId]) {
        setExpandedFolders((prev) => ({ ...prev, [targetFolderId]: true }));
      }

      const snapshot = { byContainer: updatedByContainer };
      void persistPromptMoves(Array.from(containersToPersist), snapshot);
    }
  };

  const activePrompt =
    activeType === "prompt"
      ? findPromptById(String(activeId).replace(/^prompt:/, ""))
      : null;
  const activeFolder =
    activeType === "folder"
      ? folders.find((f) => `folder:${f._id}` === activeId) || null
      : null;

  return (
    <RightPanelLayout title='推薦提示詞'>
      <div className={styles.toolbar}>
        <div className={styles.title}>推薦提示詞列表</div>
        <div className={styles.toolbarActions}>
          <Button
            type='secondary'
            title='新增資料夾'
            icon={<MdCreateNewFolder fontSize={20} />}
            onClick={openCreateFolderModal}
          />
          <Button
            type='primary'
            title='新增提示詞'
            icon={<IoAddOutline fontSize={20} />}
            onClick={() => openCreatePromptModal()}
          />
        </div>
      </div>

      <div className={styles.boardContainer}>
        {loading ? (
          <div className={styles.emptyCell}>載入中...</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
          >
            {/* Folder list */}
            <div className={styles.folderList}>
              <SortableContext
                items={folderIds}
                strategy={verticalListSortingStrategy}
              >
                {folders.map((folder) => {
                  const cid = `folder:${folder._id}`;
                  const folderPrompts = promptsByContainer[cid] || [];
                  const promptIds = folderPrompts.map(
                    (p) => `prompt:${getRowId(p)}`
                  );
                  return (
                    <FolderRow
                      key={folder._id}
                      folder={folder}
                      expanded={!!expandedFolders[folder._id]}
                      onToggle={toggleFolderExpanded}
                      onRename={openRenameFolderModal}
                      onDelete={setFolderDeleteTarget}
                      promptIds={promptIds}
                      promptsInFolder={folderPrompts}
                      onEditPrompt={openEditPromptModal}
                      onDeletePrompt={setDeleteTargetId}
                      isDropTargetActive={activeType === "prompt"}
                      hideBody={activeType === "folder"}
                    />
                  );
                })}
              </SortableContext>
              {folders.length === 0 ? (
                <div className={styles.emptyFolderHint}>
                  尚無資料夾，可點選右上角「新增資料夾」建立
                </div>
              ) : null}
            </div>

            {/* Root (unfiled) section */}
            <div className={styles.rootSection}>
              <div className={styles.sectionDivider}>
                <span>未分類</span>
                <span className={styles.sectionDividerCount}>
                  {(promptsByContainer[ROOT_CONTAINER] || []).length} 筆
                </span>
              </div>
              <FolderDropZone
                containerId={ROOT_CONTAINER}
                active={activeType === "prompt"}
                label='拖曳至此放在最前'
              />
              <SortableContext
                items={rootPromptIds}
                strategy={verticalListSortingStrategy}
              >
                {(promptsByContainer[ROOT_CONTAINER] || []).map((p, idx) => (
                  <PromptRow
                    key={getRowId(p)}
                    id={getRowId(p)}
                    item={p}
                    index={idx}
                    containerId={ROOT_CONTAINER}
                    onEdit={openEditPromptModal}
                    onDelete={setDeleteTargetId}
                  />
                ))}
              </SortableContext>
            </div>

            <DragOverlay dropAnimation={null}>
              {activeType === "prompt" && activePrompt ? (
                <PromptDragOverlay item={activePrompt} />
              ) : null}
              {activeType === "folder" && activeFolder ? (
                <FolderDragOverlay folder={activeFolder} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Prompt delete alert */}
      <Alert
        isOpen={deleteTargetId != null}
        onClose={() => !deleteSubmitting && setDeleteTargetId(null)}
        onConfirm={handleDeletePrompt}
        leftButtonText='取消'
        rightButtonText={deleteSubmitting ? "刪除中..." : "刪除"}
        mainTitle='確認刪除提示詞？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      {/* Folder delete alert */}
      <Alert
        isOpen={folderDeleteTarget != null}
        onClose={() => !folderDeleteSubmitting && setFolderDeleteTarget(null)}
        onConfirm={handleDeleteFolder}
        leftButtonText='取消'
        rightButtonText={folderDeleteSubmitting ? "刪除中..." : "刪除"}
        mainTitle={`確認刪除資料夾「${folderDeleteTarget?.name || ""}」？`}
        subTitle='資料夾內的所有提示詞將一併刪除，無法復原。'
        isWarning
      />

      {/* Folder modal */}
      <Modal
        isOpen={folderModalOpen}
        size='sm'
        width='min(480px, calc(100vw - 32px))'
        height='auto'
      >
        <div className={styles.modalRoot}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>
              {folderModalMode === "edit" ? "重新命名資料夾" : "新增資料夾"}
            </h3>
            <button
              type='button'
              className={styles.modalClose}
              onClick={closeFolderModal}
              aria-label='關閉視窗'
            >
              <MdClear size={18} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor='folder-name-input'>
                資料夾名稱
              </label>
              <input
                id='folder-name-input'
                className={styles.input}
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder='請輸入資料夾名稱'
                disabled={folderSubmitting}
              />
            </div>
          </div>
          <div className={styles.modalActions}>
            <Button type='secondary' title='取消' onClick={closeFolderModal} />
            <Button
              type='primary'
              title={folderSubmitting ? "儲存中..." : "儲存"}
              disabled={folderSubmitting}
              onClick={handleSubmitFolder}
            />
          </div>
        </div>
      </Modal>

      {/* Prompt modal */}
      <Modal
        isOpen={modalOpen}
        size='md'
        width='min(760px, calc(100vw - 32px))'
        height='auto'
      >
        <div className={styles.modalRoot}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>
              {modalMode === "edit" ? "編輯提示詞" : "新增提示詞"}
            </h3>
            <button
              type='button'
              className={styles.modalClose}
              onClick={closePromptModal}
              aria-label='關閉提示詞視窗'
            >
              <MdClear size={18} />
            </button>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor='recommended-prompt-name'>
                名稱
              </label>
              <input
                id='recommended-prompt-name'
                className={styles.input}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder='請輸入提示詞名稱'
                disabled={formSubmitting}
              />
            </div>
            <div className={styles.field}>
              <label
                className={styles.label}
                htmlFor='recommended-prompt-folder'
              >
                所屬資料夾
              </label>
              <select
                id='recommended-prompt-folder'
                className={styles.input}
                value={formFolderId}
                onChange={(e) => setFormFolderId(e.target.value)}
                disabled={formSubmitting}
              >
                <option value=''>未分類（根目錄）</option>
                {folders.map((f) => (
                  <option key={f._id} value={f._id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label
                className={styles.label}
                htmlFor='recommended-prompt-description'
              >
                描述
              </label>
              <input
                id='recommended-prompt-description'
                className={styles.input}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder='請輸入描述'
                disabled={formSubmitting}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor='recommended-prompt-content'>
                內容
              </label>
              <textarea
                id='recommended-prompt-content'
                className={styles.textarea}
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder='請輸入提示詞內容'
                disabled={formSubmitting}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor='recommended-prompt-sharedTo'>
                指定 Email（可多筆 Email）
              </label>
              <div
                className={`${styles.emailTagsContainer} ${formSubmitting ? styles.emailTagsContainerDisabled : ""}`}
                onClick={() =>
                  document.getElementById("recommended-prompt-sharedTo")?.focus()
                }
              >
                {formEmails.map((email) => (
                  <span key={email} className={styles.emailTag}>
                    <span
                      className={styles.emailTagLabel}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!formSubmitting) editFormEmail(email);
                      }}
                      role='button'
                      tabIndex={-1}
                      aria-label={`編輯 ${email}`}
                    >
                      {email}
                    </span>
                    <button
                      type='button'
                      className={styles.emailTagRemove}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFormEmail(email);
                      }}
                      disabled={formSubmitting}
                      aria-label={`移除 ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id='recommended-prompt-sharedTo'
                  className={styles.emailTagInput}
                  value={formEmailInput}
                  onChange={(e) => setFormEmailInput(e.target.value)}
                  onKeyDown={handleFormEmailKeyDown}
                  onBlur={handleFormEmailBlur}
                  placeholder={
                    formEmails.length === 0
                      ? "輸入 Email 後按 Enter/Space 或點擊其他地方"
                      : ""
                  }
                  disabled={formSubmitting}
                />
              </div>
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button type='secondary' title='取消' onClick={closePromptModal} />
            <Button
              type='primary'
              title={formSubmitting ? "儲存中..." : "儲存"}
              disabled={formSubmitting}
              onClick={handleSubmitPrompt}
            />
          </div>
        </div>
      </Modal>

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default RecommendedPromptsList;
